/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/hooks/useConnectionActions.ts
 * Description: Custom hook for device connection actions
 * Reuses logic from DeviceConnectionPanel
 */

import { useEffect, useRef } from 'react';
import { useConnectionStore } from '@/store/connectionStore';
import { useBle } from '@/connections/ble/useBle';
import { useSerial } from '@/connections/serial/useSerial';
import { useNotificationStore } from '@/store/notificationStore';
import { useAppStore } from '@/store/loadingStore';
import { useTranslation } from 'react-i18next';
import * as liteLogApi from '@/api/device/logs/liteLog';
import * as miniLogApi from '@/api/device/logs/miniLog';
import { FILE_TYPE_TO_REQUEST_FUNCTION_ID } from '@packages/types/device/lite/file_type_to_function_id';
import { MINI_FILE_TYPE_TO_REQUEST_FUNCTION_ID } from '@packages/types/device/mini/file_type_to_function_id';
import { FileType } from '@packages/types/device/lite/file_type';
import { logDownloadSessionManager } from '@/utils/logDownloadSession';
import { isCompressedGasReading } from '@/utils/deviceVersion';
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';
import type { DeviceConfiguration } from '@packages/types/device/lite/deviceConfiguration';
import type { MiniDeviceInfo } from '@packages/types/device/mini/deviceInfo';
import type { MiniDeviceConfiguration } from '@packages/types/device/mini/deviceConfiguration';
import { DeviceType, getDeviceType } from '@packages/types/device/common/deviceTypes';
import { useSavePacket } from '@/api/device/useDevice';
import { canSyncPacketToCloud } from '@/utils/cloudSyncAuth';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { parseDeviceApiError, parseDatalogApiError } from '@/utils/errors';
import { getFileTypeName } from '@/utils/datalogFileType';
import {
  inferProtocolLogConnectionType,
  protocolUsesWirelessStyleDeviceName,
  type LiteLogActiveConnectionType,
  type UiConnectedConnectionType,
} from '@/types/connection';

type AnyDeviceInfo = DeviceInfo | MiniDeviceInfo;
type AnyDeviceConfiguration = DeviceConfiguration | MiniDeviceConfiguration;

/** DEV: concurrent refreshDeviceInfo calls (e.g. auto-refresh + page useEffect) */
let __refreshDeviceInfoDepth = 0;

/**
 * Compare firmware versions (e.g., "v1.0.7" vs "v1.0.6")
 * @returns positive if v1 > v2, negative if v1 < v2, 0 if equal
 */
function compareFirmwareVersion(v1: string, v2: string): number {
  // Remove 'v' prefix and split by '.'
  const parse = (v: string) => {
    const clean = v.replace(/^v/, '');
    return clean.split('.').map(n => parseInt(n, 10) || 0);
  };

  const parts1 = parse(v1);
  const parts2 = parse(v2);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    if (num1 !== num2) {
      return num1 - num2;
    }
  }
  return 0;
}

/**
 * Custom hook for connection actions
 * Provides all connection/disconnection handlers with callback support
 *
 * @param onDeviceLoaded - Callback when device data is loaded
 * @param onConnectionChange - Callback when connection type changes
 * @param onDisconnected - Callback when device is disconnected
 * @param autoRefreshOnMount - If true, automatically refresh device data on mount and connection changes
 */
export const useConnectionActions = (
  onDeviceLoaded?: (deviceInfo: AnyDeviceInfo, deviceConfig: AnyDeviceConfiguration) => void,
  onConnectionChange?: (connectionType: UiConnectedConnectionType) => void,
  onDisconnected?: () => void,
  autoRefreshOnMount?: boolean
) => {
  const { t } = useTranslation('device');
  const ble = useBle();
  const serial = useSerial();
  const { showSuccess, showError, showWarning } = useNotificationStore();
  const { showLoading, hideLoading, setProgress } = useAppStore();
  const savePacketMutation = useSavePacket();

  // Get connection store state and actions
  const protocol = useConnectionStore(state => state.protocol);
  const device = useConnectionStore(state => state.device);
  const connectBle = useConnectionStore(state => state.connectBle);
  const connectSerial = useConnectionStore(state => state.connectSerial);
  const disconnect = useConnectionStore(state => state.disconnect);
  const confirmDevice = useConnectionStore(state => state.confirmDevice);
  const setDownloading = useConnectionStore(state => state.setDownloading);

  // Ref to track if auto refresh has been performed
  const hasAutoRefreshedRef = useRef(false);

  /**
   * Save device information and configuration to database
   * Converts deviceInfo and deviceConfig to packets and sends to backend
   */
  const saveDeviceDataToDb = async (
    serialNumber: string,
    deviceInfoPacket: number[] | null,
    deviceConfigPacket: number[] | null
  ) => {
    // Cloud packet API requires Bearer; configuration can be used without login
    if (!canSyncPacketToCloud()) {
      return;
    }

    try {
      // Save device information packet (class_id: 1, function_id: 1)
      if (deviceInfoPacket && deviceInfoPacket.length > 0) {
        await savePacketMutation.mutateAsync({
          serial_number: serialNumber,
          packet: deviceInfoPacket,
        });
      }

      // Save device configuration packet (class_id: 3, function_id: 1)
      if (deviceConfigPacket && deviceConfigPacket.length > 0) {
        await savePacketMutation.mutateAsync({
          serial_number: serialNumber,
          packet: deviceConfigPacket,
        });
      }
    } catch (error) {
      console.error('❌ [saveDeviceDataToDb] Failed to save device data:', error);
      // Don't throw error - just log it, as this is a background operation
    }
  };

  /**
   * Handle Bluetooth connection
   */
  const handleBluetoothConnect = async () => {
    try {
      showLoading(t('connectingBluetooth'));

      const success = await ble.scanAndConnect();

      if (!success) {
        hideLoading();
        showError(i18nKeys.device.bluetoothFailed, i18nKeys.device.bluetoothFailedDesc);
        return;
      }

      // 기기 정보 요청 (재시도 로직 포함)
      let deviceInfoResult;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          ble.clearBuffer();

          deviceInfoResult = await ble.connectDevice({
            class_id: 1,
            function_id: 1,
            json_payload: {},
          });

          if (deviceInfoResult?.json && deviceInfoResult.json.serial_number) {
            break;
          } else {
            retryCount++;
            if (retryCount >= maxRetries) {
              throw new Error('Device info response missing serial_number after all retries');
            }
            await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
            continue;
          }
        } catch (error) {
          retryCount++;

          if (retryCount >= maxRetries) {
            throw error;
          }

          await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
        }
      }

      if (!deviceInfoResult?.json) {
        hideLoading();
        showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.deviceInfoFailedDesc);
        return;
      }

      const deviceInfo = deviceInfoResult.json as unknown as AnyDeviceInfo;
      const deviceName = ble.getSelectedDeviceName();

      // serial_number 필수 검증
      if (!deviceInfo.serial_number) {
        console.error('❌ Device info missing serial_number:', deviceInfo);
        hideLoading();
        showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.serialNumberMissing);
        return;
      }

      const deviceId = deviceInfo.serial_number;
      const fwVersion = deviceInfo.fw_version || 'v0.0.0';

      // Get device configuration (class_id: 3, function_id: 1)
      const configResult = await ble.connectDevice(
        {
          class_id: 3,
          function_id: 1,
          json_payload: {},
        },
        deviceInfo as unknown as DeviceInfo
      );

      const deviceConfig = (configResult?.json || null) as unknown as AnyDeviceConfiguration | null;

      const detectedDeviceType = getDeviceType({ serial_number: deviceId });

      await connectBle({
        sessionId: crypto.randomUUID(),
        device: {
          type: detectedDeviceType,
          activeDeviceId: deviceId,
          deviceName: deviceName || null,
          fwVersion,
          info: deviceInfo,
          config: (deviceConfig ?? {}) as AnyDeviceConfiguration,
        },
      });

      // Save device data to database (background operation)
      // Get original packets from the response
      const deviceInfoPacket = deviceInfoResult?.packet || null;
      const deviceConfigPacket = configResult?.packet || null;

      if (deviceInfoPacket || deviceConfigPacket) {
        saveDeviceDataToDb(deviceId, deviceInfoPacket, deviceConfigPacket).catch(error => {
          console.error('❌ Failed to save device data to DB:', error);
        });
      }

      hideLoading();
      showSuccess(i18nKeys.device.bluetoothConnected, i18nKeys.device.bluetoothConnected);

      // Callback: Device loaded with info and config
      if (onDeviceLoaded && deviceConfig) {
        onDeviceLoaded(deviceInfo, deviceConfig);
      }

      // Callback: Connection type changed
      if (onConnectionChange) {
        onConnectionChange('bluetooth');
      }
    } catch (error: unknown) {
      hideLoading();
      console.error('❌ Bluetooth connection failed:', error);
      showError(i18nKeys.device.bluetoothFailed, i18nKeys.device.bluetoothFailedDesc);
    }
  };

  /**
   * Handle Cradle connection
   */
  const handleCradleConnect = async () => {
    try {
      showLoading(t('connectingCradle'));

      const connected = await serial.scanAndConnect();

      if (!connected) {
        hideLoading();
        showError(
          i18nKeys.device.cradleConnectionFailed,
          i18nKeys.device.cradleConnectionFailedDesc
        );
        return;
      }

      await connectSerial({
        sessionId: crypto.randomUUID(),
        portName: 'serial-port',
        adapter: { type: 'cradle' },
      });

      hideLoading();
      showSuccess(i18nKeys.device.cradleConnected, i18nKeys.device.cradleConnected);
    } catch (error: unknown) {
      hideLoading();
      console.error('❌ Cradle connection failed:', error);
      showError(i18nKeys.device.cradleConnectionFailed, i18nKeys.device.cradleConnectionFailedDesc);
    }
  };

  /**
   * Handle disconnect
   */
  const handleDisconnect = async () => {
    try {
      showLoading(t('disconnecting'));

      if (protocol.type === 'ble') {
        ble.disconnect();
      } else if (protocol.type === 'serial') {
        serial.disconnect();
      }

      await disconnect();

      hideLoading();
      showSuccess(i18nKeys.device.disconnected, i18nKeys.device.disconnectedDesc);

      // Callback: Disconnected
      if (onDisconnected) {
        onDisconnected();
      }
    } catch (error: unknown) {
      hideLoading();
      console.error('❌ Disconnect failed:', error);
      showError(i18nKeys.device.disconnectFailed, i18nKeys.device.disconnectFailed);
    }
  };

  /**
   * Handle load device from cradle
   * Loads both deviceInfo and deviceConfiguration
   */
  const handleLoadDevice = async () => {
    try {
      showLoading(t('loadingDeviceFromCradle'));

      // 버퍼 초기화
      serial.clearBuffer();

      // 1. Device Info 가져오기 (class_id: 1, function_id: 1)
      const deviceInfoResult = await serial.connectDevice({
        class_id: 1,
        function_id: 1,
        json_payload: {},
      });

      if (!deviceInfoResult?.json) {
        hideLoading();
        showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.deviceInfoFailedDesc);
        return;
      }

      const deviceInfo = deviceInfoResult.json as unknown as AnyDeviceInfo;
      const deviceId = deviceInfo.serial_number;
      const fwVersion = deviceInfo.fw_version || 'v0.0.0';

      // 2. Device Configuration 가져오기 (class_id: 3, function_id: 1)
      const configResult = await serial.connectDevice(
        {
          class_id: 3,
          function_id: 1,
          json_payload: {},
        },
        deviceInfo as unknown as DeviceInfo
      );

      if (!configResult?.json) {
        hideLoading();
        showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.deviceInfoFailedDesc);
        return;
      }

      const deviceConfig = configResult.json as any;

      // 3. Connection Store에 기기 정보 확정 저장
      const detectedDeviceType = getDeviceType({ serial_number: deviceId });
      confirmDevice({
        type: detectedDeviceType,
        activeDeviceId: deviceId,
        deviceName: null,
        fwVersion,
        info: deviceInfo,
        config: deviceConfig,
      });

      // 4. Save device data to database (background operation)
      // Get original packets from the response
      const deviceInfoPacket = deviceInfoResult?.packet || null;
      const deviceConfigPacket = configResult?.packet || null;

      if (deviceInfoPacket || deviceConfigPacket) {
        saveDeviceDataToDb(deviceId, deviceInfoPacket, deviceConfigPacket).catch(error => {
          console.error('❌ Failed to save device data to DB:', error);
        });
      }

      hideLoading();
      showSuccess(i18nKeys.device.deviceLoaded, i18nKeys.device.deviceLoaded);

      // Callback: Device loaded with deviceInfo and deviceConfig
      if (onDeviceLoaded) {
        onDeviceLoaded(deviceInfo, deviceConfig);
      }
    } catch (error: unknown) {
      hideLoading();
      const e = parseDeviceApiError(error);
      showError(e.title, e.message);
    }
  };

  /**
   * Handle scan another device
   */
  const handleScanAnother = async () => {
    try {
      if (protocol.type === 'ble') {
        showLoading(t('disconnecting'));
        await ble.disconnect();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 2. Connection Store 초기화
      await disconnect();

      // 3. 부모 컴포넌트에 연결 해제 알림
      if (onDisconnected) {
        onDisconnected();
      }

      // 5. 새로운 디바이스 연결 (handleBluetoothConnect 재사용)
      await handleBluetoothConnect();
    } catch (error: unknown) {
      hideLoading();
      const e = parseDeviceApiError(error);
      showError(i18nKeys.device.scanFailed, e.message);
    }
  };

  /**
   * Refresh device info from currently connected device
   * Returns updated deviceInfo and deviceConfig
   */
  const refreshDeviceInfo = async () => {
    if (import.meta.env.DEV) {
      __refreshDeviceInfoDepth += 1;
      if (__refreshDeviceInfoDepth > 1) {
        console.warn(
          '[useConnectionActions] refreshDeviceInfo OVERLAP — concurrent calls may corrupt serial traffic',
          { depth: __refreshDeviceInfoDepth }
        );
      }
    }
    try {
      try {
        showLoading(t('refreshingDeviceInfo'));

        let deviceInfo = null;
        let deviceConfig = null;
        let deviceInfoPacket = null;
        let deviceConfigPacket = null;

        if (protocol.type === 'serial') {
          serial.clearBuffer();

          const deviceInfoResult = await serial.connectDevice({
            class_id: 1,
            function_id: 1,
            json_payload: {},
          });

          if (deviceInfoResult?.json) {
            deviceInfo = deviceInfoResult.json;
            deviceInfoPacket = deviceInfoResult.packet || null;

            const configResult = await serial.connectDevice({
              class_id: 3,
              function_id: 1,
              json_payload: {},
            });

            if (configResult?.json) {
              deviceConfig = configResult.json;
              deviceConfigPacket = configResult.packet || null;
            }
          }
        } else if (protocol.type === 'ble') {
          const deviceInfoResult = await ble.connectDevice({
            class_id: 1,
            function_id: 1,
            json_payload: {},
          });

          if (deviceInfoResult?.json) {
            deviceInfo = deviceInfoResult.json;
            deviceInfoPacket = deviceInfoResult.packet || null;

            const configResult = await ble.connectDevice(
              {
                class_id: 3,
                function_id: 1,
                json_payload: {},
              },
              deviceInfo as unknown as DeviceInfo
            );

            if (configResult?.json) {
              deviceConfig = configResult.json;
              deviceConfigPacket = configResult.packet || null;
            }
          }
        } else {
          hideLoading();
          showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
          return { success: false, error: 'Not connected' };
        }

        if (!deviceInfo) {
          hideLoading();
          showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.deviceInfoFailedDesc);
          return { success: false, error: 'Failed to get device info' };
        }

        const serialNumber = (deviceInfo as unknown as AnyDeviceInfo).serial_number;

        // 기기 정보 store 업데이트
        const refreshedDeviceType = getDeviceType({ serial_number: serialNumber });
        const currentDeviceName =
          protocolUsesWirelessStyleDeviceName(protocol) && device.type !== 'none'
            ? device.deviceName
            : null;
        confirmDevice({
          type: refreshedDeviceType,
          activeDeviceId: serialNumber,
          deviceName: currentDeviceName,
          fwVersion: (deviceInfo as unknown as AnyDeviceInfo).fw_version || 'v0.0.0',
          info: deviceInfo as unknown as AnyDeviceInfo,
          config: deviceConfig as AnyDeviceConfiguration,
        });

        // Save device data to database (background operation)
        if (deviceInfoPacket || deviceConfigPacket) {
          saveDeviceDataToDb(serialNumber, deviceInfoPacket, deviceConfigPacket).catch(error => {
            console.error('❌ Failed to save device data to DB:', error);
          });
        }

        hideLoading();
        showSuccess(i18nKeys.device.deviceInfoRefreshed, i18nKeys.device.deviceInfoRefreshed);

        return { success: true, data: { deviceInfo, deviceConfig } };
      } catch (error: unknown) {
        hideLoading();
        console.error('❌ Refresh device info failed:', error);
        const e = parseDeviceApiError(error);
        showError(i18nKeys.device.refreshFailed, e.message);
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
      }
    } finally {
      if (import.meta.env.DEV) {
        __refreshDeviceInfoDepth -= 1;
      }
    }
  };

  /**
   * Save device configuration to connected device
   */
  const saveDeviceConfig = async (
    configData: AnyDeviceConfiguration,
    deviceInfo: AnyDeviceInfo
  ) => {
    try {
      showLoading(t('savingConfig'));

      let result;

      if (protocol.type === 'serial') {
        // Serial 연결 상태 확인
        if (!serial.isConnected()) {
          hideLoading();
          showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
          return { success: false, error: 'Serial not connected' };
        }

        result = await serial.connectDevice(
          {
            class_id: 3,
            function_id: 3,
            json_payload: configData as unknown as Record<string, unknown>,
          },
          deviceInfo as unknown as DeviceInfo
        );
      } else if (protocol.type === 'ble') {
        // BLE 연결 상태 확인
        if (!ble.isConnected()) {
          hideLoading();
          showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
          return { success: false, error: 'Bluetooth not connected' };
        }

        result = await ble.connectDevice(
          {
            class_id: 3,
            function_id: 3,
            json_payload: configData as unknown as Record<string, unknown>,
          },
          deviceInfo as unknown as DeviceInfo
        );
      } else {
        hideLoading();
        showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
        return { success: false, error: 'Not connected' };
      }

      hideLoading();

      // 성공 조건: result.json.result === 1 또는 result.json이 존재하면 성공으로 간주
      if (result?.json && (result.json.result === 1 || result.json.result === undefined)) {
        showSuccess(i18nKeys.config.msg_configSaved, i18nKeys.config.msg_configSaved);
        return { success: true };
      } else {
        showError(i18nKeys.config.msg_configSaveFailed, i18nKeys.config.msg_configSaveFailed);
        return { success: false, error: 'Save failed' };
      }
    } catch (error: unknown) {
      hideLoading();
      console.error('❌ Save config failed:', error);
      const e = parseDeviceApiError(error);
      showError(i18nKeys.config.msg_configSaveFailed, e.message);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  /**
   * Load default configuration from connected device
   */
  const loadDefaultConfig = async () => {
    try {
      showLoading(t('loadingDefaultConfig'));

      let deviceInfo = null;

      if (protocol.type === 'serial') {
        const result = await serial.connectDevice({ class_id: 1, function_id: 1 });
        if (result?.json) {
          deviceInfo = result.json;
        }
      } else if (protocol.type === 'ble') {
        const result = await ble.connectDevice({ class_id: 1, function_id: 1 });
        if (result?.json) {
          deviceInfo = result.json;
        }
      } else {
        hideLoading();
        showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
        return { success: false, error: 'Not connected' };
      }

      if (!deviceInfo) {
        hideLoading();
        showError(i18nKeys.device.deviceInfoFailed, i18nKeys.device.deviceInfoFailedDesc);
        return { success: false, error: 'Failed to get device info' };
      }

      // 기기 정보 store 업데이트 (config 없이 info만 갱신)
      const defaultConfigDeviceType = getDeviceType({
        serial_number: (deviceInfo as unknown as AnyDeviceInfo).serial_number,
      });
      const existingConfig =
        device.type !== 'none' ? device.config : ({} as AnyDeviceConfiguration);
      const existingDeviceName = device.type !== 'none' ? device.deviceName : null;
      confirmDevice({
        type: defaultConfigDeviceType,
        activeDeviceId: (deviceInfo as unknown as AnyDeviceInfo).serial_number,
        deviceName: existingDeviceName,
        fwVersion: (deviceInfo as unknown as AnyDeviceInfo).fw_version || 'v0.0.0',
        info: deviceInfo as unknown as AnyDeviceInfo,
        config: existingConfig,
      });

      hideLoading();
      showSuccess(i18nKeys.config.msg_defaultValueLoaded, i18nKeys.config.msg_defaultValueLoaded);

      return { success: true, data: { deviceInfo } };
    } catch (error: unknown) {
      hideLoading();
      console.error('❌ Load default config failed:', error);
      const e = parseDeviceApiError(error);
      showError(i18nKeys.config.msg_defaultValueLoadingFailed, e.message);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  /**
   * Auto refresh device info on mount or connection change
   * Triggers when autoRefreshOnMount is enabled and a device is connected
   */
  useEffect(() => {
    // Skip if auto refresh is not enabled
    if (!autoRefreshOnMount) return;

    // Reset flag when disconnected
    if (protocol.type === 'none') {
      hasAutoRefreshedRef.current = false;
      return;
    }

    // Skip if already refreshed
    if (hasAutoRefreshedRef.current) return;

    // Skip network refresh if device info already exists in store (e.g., from page redirect)
    if (device.type !== 'none') {
      hasAutoRefreshedRef.current = true;
      // Trigger callback with existing data
      if (onDeviceLoaded) {
        onDeviceLoaded(device.info, device.config);
      }
      return;
    }

    // Mark as refreshed to prevent duplicate calls
    hasAutoRefreshedRef.current = true;

    const performAutoRefresh = async () => {
      try {
        const result = await refreshDeviceInfo();

        if (result.success && result.data) {
          // Trigger callback with fresh data
          if (onDeviceLoaded) {
            onDeviceLoaded(
              result.data.deviceInfo as unknown as AnyDeviceInfo,
              result.data.deviceConfig as unknown as AnyDeviceConfiguration
            );
          }
        } else {
          // Error handling: disconnect and reset
          console.error('❌ [useConnectionActions] Auto refresh failed:', result.error);
          showError(i18nKeys.device.refreshFailed, i18nKeys.device.refreshFailed);
          await handleDisconnect();
        }
      } catch (error: unknown) {
        console.error('❌ [useConnectionActions] Auto refresh error:', error);
        const e = parseDeviceApiError(error);
        showError(i18nKeys.device.refreshFailed, e.message);
        await handleDisconnect();
      }
    };

    performAutoRefresh();
  }, [protocol.type, device.type, autoRefreshOnMount, onDeviceLoaded]);

  /**
   * Helper: LITE 로그 다운로드 (cradle/BLE 공통)
   * cradle과 bluetooth-LITE 경로는 연결 객체와 디바이스 정보 획득 방식만 다르고
   * 이후 로그 요청/청크 저장/누락 청크 처리/완료 처리 로직은 동일하므로 공통 함수로 추출
   */
  const downloadLiteLogViaConnection = async (
    connection: typeof serial | typeof ble,
    isSerialConnection: boolean,
    deviceInfo: any,
    deviceFwVersion: string | undefined,
    context: {
      logApi: any;
      logId: string;
      logTypeName: string;
      actualFileType: number;
      params: {
        serialNumber: string;
        daysOfGasReading?: number;
        transferMode: number;
        onProgress?: (progress: number, current: number, total: number) => void;
      };
      handleCancel: () => Promise<void>;
      isCancelledRef: { current: boolean };
      progressIntervalRef: { current: NodeJS.Timeout | null };
    }
  ) => {
    const { logApi, logId, logTypeName, actualFileType, params, handleCancel } = context;

    // Request log download
    showLoading(`${logTypeName}\nRequesting log download... 0%`, true, handleCancel);
    setProgress(0);

    const onProgressCallback = (progress: number, current: number, total: number) => {
      if (context.isCancelledRef.current) return;
      showLoading(
        `${logTypeName}\nRequesting log download... ${Math.round(progress * 10) / 10}%`,
        true,
        handleCancel
      );
      setProgress(Math.round(progress * 10) / 10);
      if (params.onProgress) {
        params.onProgress(progress, current, total);
      }
    };

    const requestDto = {
      class_id: 16,
      function_id: 1,
      json_payload: {
        file_type: actualFileType,
        days_of_gas_reading: params.daysOfGasReading || 1,
        transfer_mode: params.transferMode,
      },
    };

    let logResponse;
    try {
      // serial.connectDevice: (dto, deviceInfo, sessionId?, onProgress?)
      // ble.connectDevice: (dto, deviceInfo, onProgress?) — 인자 개수가 다르므로 분기 필요
      logResponse = isSerialConnection
        ? await (connection as typeof serial).connectDevice(
            requestDto,
            deviceInfo,
            undefined,
            onProgressCallback
          )
        : await (connection as typeof ble).connectDevice(
            requestDto,
            deviceInfo,
            onProgressCallback
          );
    } finally {
      if (context.progressIntervalRef.current) {
        clearInterval(context.progressIntervalRef.current);
        context.progressIntervalRef.current = null;
      }
    }

    // Check if cancelled after device communication
    if (context.isCancelledRef.current) {
      throw new Error('Download cancelled by user');
    }

    const responseJson = logResponse.json as any;

    showLoading(`${logTypeName}\nLog download complete!`);

    await logApi.updateLogInfo(logId, {
      version:
        typeof responseJson.version === 'number'
          ? responseJson.version
          : parseInt(responseJson.version) || 0,
      num_packets:
        typeof responseJson.num_packets === 'number'
          ? responseJson.num_packets
          : parseInt(responseJson.num_packets) || 0,
      total_size:
        typeof responseJson.total_size === 'number'
          ? responseJson.total_size
          : parseInt(responseJson.total_size) || 0,
      status: 'downloading',
    });

    const numPackets = responseJson.num_packets;
    const dataPackets = (logResponse.json as any).dataPackets as any[];

    if (!dataPackets || !Array.isArray(dataPackets)) {
      throw new Error('No chunk data received');
    }

    // Check if cancelled before processing chunks
    if (context.isCancelledRef.current) {
      throw new Error('Download cancelled by user');
    }

    showLoading(`${logTypeName}\nReceiving data from device 100%\nPreparing chunk data...`);
    setProgress(100);

    // Save chunks in batches
    // Gas reading (file_type=0) with FW >= 5.0.0: use compressed API
    const isGasReading = actualFileType === 0;
    const useCompressedApi = isGasReading && isCompressedGasReading(deviceFwVersion);

    if (useCompressedApi) {
      await saveChunksBatchCompressed(
        logApi,
        logId,
        params.serialNumber,
        dataPackets,
        numPackets,
        deviceFwVersion!,
        logTypeName
      );
    } else {
      await saveChunksBatch(
        logApi,
        logId,
        params.serialNumber,
        dataPackets,
        numPackets,
        actualFileType,
        false,
        logTypeName
      );
    }

    // Handle missing chunks
    await handleMissingChunks(
      connection,
      logApi,
      logId,
      params.serialNumber,
      numPackets,
      actualFileType,
      false,
      deviceInfo,
      logTypeName
    );

    // Final backend validation
    try {
      const missingChunks = await logApi.getMissingChunks(logId);
      if (missingChunks.length > 0) {
        console.error(`❌ Backend still has missing chunks:`, missingChunks);
        const errorMessage = `Backend validation failed: ${missingChunks.length} chunks missing [${missingChunks.join(', ')}]`;
        await logApi.failLog(logId, errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : '';
      if (errMsg.includes('Backend validation failed')) {
        throw error;
      }
      console.warn(`⚠️ Missing chunk check failed (continuing):`, error);
    }

    showLoading('Log download complete!\nFinalizing...');
    await logApi.completeLog(logId);

    showSuccess(i18nKeys.datalog.logDataSaved, i18nKeys.datalog.logDataSaved);
  };

  /**
   * Download datalog from connected device
   * Supports both MINI and LITE devices, cradle and BLE connections
   *
   * @param params - Download parameters
   * @param params.serialNumber - Device serial number
   * @param params.deviceType - 'bellmini' or 'belllite'
   * @param params.fileType - File type to download (0: datalog, 1: eventlog, etc)
   * @param params.fileTypes - Array of file types for batch download
   * @param params.daysOfGasReading - Days of gas reading (LITE only)
   * @param params.transferMode - Transfer mode (0: normal, 1: burst)
   * @param params.onProgress - Progress callback
   * @param existingLogId - Optional existing log ID to reuse
   */
  const downloadDatalog = async (
    params: {
      serialNumber: string;
      deviceType: 'bellmini' | 'belllite';
      connectionType?: LiteLogActiveConnectionType;
      fileType?: number;
      fileTypes?: number[];
      daysOfGasReading?: number;
      transferMode: number;
      onProgress?: (progress: number, current: number, total: number) => void;
      isBatchMode?: boolean; // 배치 모드 플래그 (전체 로그 다운로드 시 true)
    },
    existingLogId?: string
  ) => {
    const isMini = params.deviceType === 'bellmini';
    let logId: string | null = null;
    const isCancelledRef = { current: false };
    const progressIntervalRef: { current: NodeJS.Timeout | null } = { current: null };

    // Determine actual file type
    const actualFileType =
      params.fileType !== undefined
        ? params.fileType
        : params.fileTypes && params.fileTypes.length > 0
          ? params.fileTypes[0]
          : undefined;

    if (actualFileType === undefined) {
      throw new Error('file_type or file_types is required');
    }

    const logTypeName = getFileTypeName(actualFileType);

    // Auto-detect connection type if not provided (used by cancel + log creation)
    const actualConnectionType = params.connectionType || inferProtocolLogConnectionType(protocol);

    // Cancel handler
    const handleCancel = async () => {
      isCancelledRef.current = true;

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      try {
        // Send stop burst mode command based on connection type
        if (actualConnectionType === 'cradle' && serial.isConnected()) {
          await serial.cancelDownload();
        } else if (actualConnectionType === 'bluetooth' && ble.isConnected()) {
          await ble.cancelDownload();
        }
      } catch (error) {
        console.error('❌ [Cancel] Failed to send stop burst mode command:', error);
      }

      if (logId) {
        try {
          const logApi = isMini ? miniLogApi : liteLogApi;
          await logApi.failLog(logId, 'User cancelled download');
        } catch (error) {
          console.error('❌ [Cancel] Failed to mark log as failed:', error);
        }
      }

      // 배치 모드가 아닐 때만 즉시 상태 초기화
      // 배치 모드에서는 for 루프의 catch 블록에서 나머지 로그를 정리한 후 초기화
      if (!params.isBatchMode) {
        setDownloading(false);
        hideLoading();
        showError(i18nKeys.datalog.logDownloadCancelled, i18nKeys.datalog.downloadingDataDeleted);
      }
    };

    try {
      // Set downloading state to prevent data refresh (배치 모드가 아닐 때만)
      if (!params.isBatchMode) {
        setDownloading(true);
      }

      // Validate connection
      if (actualConnectionType === 'none') {
        throw new Error('No device connected');
      }

      // 1. Create log request
      if (existingLogId) {
        logId = existingLogId;
      } else {
        showLoading('Creating log request...', true, handleCancel);

        const logInfo = isMini
          ? await miniLogApi.createLogRequest({
              serial_number: params.serialNumber,
              file_type: actualFileType,
              transfer_mode: params.transferMode,
              connection_type: 'bluetooth',
            })
          : await liteLogApi.createLogRequest({
              serial_number: params.serialNumber,
              file_type: actualFileType,
              days_requested: params.daysOfGasReading || 1,
              transfer_mode: params.transferMode,
              connection_type: actualConnectionType === 'bluetooth' ? 'bluetooth' : 'cradle',
            });

        logId = logInfo.log_id;
      }

      if (!logId) {
        throw new Error('Failed to create or retrieve log ID');
      }

      const logApi = isMini ? miniLogApi : liteLogApi;

      // MINI burst mode requirements:
      // 1. Firmware version >= 1.0.7
      // 2. File type = 0 (Datalog only)
      let actualTransferMode = params.transferMode;
      if (isMini) {
        const fwVersion = device.type !== 'none' ? device.fwVersion : 'v0.0.0';
        const isVersionSupported = compareFirmwareVersion(fwVersion, 'v1.0.7') >= 0;
        const isDatalog = actualFileType === 0;

        if (!isVersionSupported || !isDatalog) {
          actualTransferMode = 0; // Force single chunk mode
          if (params.transferMode === 1) {
          }
        }
      }

      // 2. Download based on connection type
      if (actualConnectionType === 'cradle') {
        // CRADLE CONNECTION (LITE only)
        showLoading('Checking cradle connection...');

        if (serial.portState !== 'open') {
          showLoading('Connecting to cradle...');
          const success = await serial.scanAndConnect();
          if (!success) {
            throw new Error('Failed to connect to cradle');
          }
        }

        showLoading('Checking device info...');
        serial.clearBuffer();
        const deviceInfoResult = await serial.connectDevice({ class_id: 1, function_id: 1 });

        if (!deviceInfoResult?.json) {
          throw new Error('Cannot load device info from cradle');
        }

        const deviceInfo = deviceInfoResult.json as any;
        const connectedSerialNumber = deviceInfo.serial_number as string;

        if (connectedSerialNumber !== params.serialNumber) {
          throw new Error('Device serial number mismatch');
        }

        const deviceFwVersion = deviceInfo.fw_version || deviceInfo.FW_Version;

        await downloadLiteLogViaConnection(serial, true, deviceInfo, deviceFwVersion, {
          logApi,
          logId,
          logTypeName,
          actualFileType,
          params,
          handleCancel,
          isCancelledRef,
          progressIntervalRef,
        });
      } else if (actualConnectionType === 'bluetooth') {
        // BLUETOOTH (Class 16 / LogDownloadHandler paths)
        // ⏱️ 통신 시간 측정 시작
        // const deviceCommStartTime = performance.now();

        const wireless = ble;

        showLoading('Checking Bluetooth connection...');
        const isConnected = ble.isConnected();

        if (!isConnected) {
          showLoading('Connecting via Bluetooth...');
          const deviceFilter = isMini ? DeviceType.MINI : DeviceType.LITE;
          const success = await ble.scanAndConnect(deviceFilter, params.serialNumber);
          if (!success) {
            throw new Error('Bluetooth connection failed');
          }
        }

        // MINI: Use LogDownloadHandler
        if (isMini) {
          // Clear buffer before starting download
          showLoading('Preparing for log download...');
          wireless.clearBuffer();

          // Small delay to ensure buffer is cleared
          await new Promise(resolve => setTimeout(resolve, 100));

          const sessionId = logDownloadSessionManager.createSession(params.serialNumber, 'ble', [
            actualFileType,
          ]);

          const downloadRequest = {
            serial_number: params.serialNumber,
            file_type: actualFileType,
            transfer_mode: actualTransferMode,
            connection_type: 'ble' as const,
            log_id: logId,
          };

          const batchUploadCallback = async (
            logId: string,
            serialNumber: string,
            chunks: Array<{
              chunk_index: number;
              chunk_data: number[];
              crc?: number;
            }>
          ) => {
            await miniLogApi.saveLogChunkBatch({
              log_id: logId,
              serial_number: serialNumber,
              chunks,
            });
          };

          // ⏱️ 서버 전송 시간 측정 시작 (MINI는 downloadLog 내부에서 업로드)
          // const uploadStartTime = performance.now();

          const downloadResult = await wireless.downloadLog(
            downloadRequest,
            sessionId,
            (progress, current, total) => {
              showLoading(`${logTypeName}\nDownloading... ${current}/${total}`, true, handleCancel);
              if (params.onProgress) {
                params.onProgress(progress, current, total);
              }
            },
            undefined,
            batchUploadCallback,
            20
          );

          // Check if cancelled after download
          if (isCancelledRef.current) {
            throw new Error('Download cancelled by user');
          }

          // ⏱️ 통신 + 전송 시간 측정 완료
          // const deviceCommEndTime = performance.now();
          // const totalCommDuration = deviceCommEndTime - deviceCommStartTime;
          // const uploadDuration = deviceCommEndTime - uploadStartTime;
          // const deviceCommDuration = totalCommDuration - uploadDuration;

          //ZUSTAND

          if (!downloadResult.success) {
            throw new Error(downloadResult.error_message || 'Log download failed');
          }

          // Complete the log (validates all chunks are received)
          showLoading(`${logTypeName}\nCompleting download...`);
          try {
            await miniLogApi.completeLog(logId);
          } catch (completeError: unknown) {
            const axiosComplete = completeError as {
              message?: string;
              response?: { data?: unknown };
            };
            console.error('❌ [completeLog] 완료 처리 실패:', {
              logId,
              error: completeError,
              message:
                completeError instanceof Error ? completeError.message : axiosComplete.message,
              response: axiosComplete.response?.data,
            });
            throw new Error(
              `Log completion failed: ${
                completeError instanceof Error
                  ? completeError.message
                  : axiosComplete.message || 'Unknown error'
              }`
            );
          }

          // 파싱은 백엔드 completeLog 직후 비동기로 진행 (Lite와 동일). 긴 POST /parse 대기 제거.

          showLoading(`${logTypeName}\nLog download complete!`);

          // ⏱️ 전체 시간 출력 (MINI BLE)
          const totalRecords = (downloadResult as any).total_packets || 0;
          if (totalRecords > 0) {
          }

          showSuccess(i18nKeys.datalog.logDataSaved, i18nKeys.datalog.logDataSaved);

          // MINI download complete - return early
          return { success: true, logId };
        }

        // LITE: Traditional method
        let connectedDeviceInfo: any;
        let deviceFwVersion: string | undefined;

        if (device.type !== 'none') {
          connectedDeviceInfo = device.info;
          deviceFwVersion = device.fwVersion; // store에 저장된 FW 버전
        } else {
          showLoading('Checking device info...');
          wireless.clearBuffer();

          const deviceInfoResult = await wireless.connectDevice({ class_id: 1, function_id: 1 });

          if (!deviceInfoResult?.json) {
            throw new Error('Cannot load device info via Bluetooth');
          }

          connectedDeviceInfo = deviceInfoResult.json as any;
          deviceFwVersion = connectedDeviceInfo.fw_version || connectedDeviceInfo.FW_Version; // BLE: deviceInfo에 포함된 FW 버전

          const connectedSerialNumber = connectedDeviceInfo.serial_number as string;

          if (connectedSerialNumber !== params.serialNumber) {
            throw new Error('Device serial number mismatch');
          }
        }

        await downloadLiteLogViaConnection(wireless, false, connectedDeviceInfo, deviceFwVersion, {
          logApi,
          logId,
          logTypeName,
          actualFileType,
          params,
          handleCancel,
          isCancelledRef,
          progressIntervalRef,
        });
      }

      return { success: true, logId };
    } catch (error: unknown) {
      console.error('❌ [downloadDatalog] Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Log download failed';

      if (logId) {
        const logApi = isMini ? miniLogApi : liteLogApi;
        await logApi.failLog(logId, errorMessage).catch(console.error);
      }

      const e = parseDatalogApiError(error);
      if (e.type === 'warning') {
        showWarning(e.title, e.message);
      } else {
        showError(e.title, e.message);
      }
      return { success: false, error: errorMessage };
    } finally {
      // Clear downloading state (배치 모드가 아닐 때만)
      if (!params.isBatchMode) {
        setDownloading(false);
        hideLoading();
      }
    }
  };

  /**
   * Helper: Get expected log_type for validation
   */
  const getExpectedLogType = (fileType: number, isMini: boolean = false): number | null => {
    if (isMini) {
      // MINI protocol: DATALOG (0), EVENTLOG (1), CAL_BUMP (2) don't have consistent log_type
      return null;
    } else {
      // LITE protocol
      switch (fileType) {
        case 0: // GAS (DATALOG)
          return 0x01;
        case 1: // EVENT
          return 0x02;
        case 2: // GEN_CONF - no log_type
          return null;
        case 3: // SEN_CONF - no log_type
          return null;
        case 4: // GAS_ALARM - no log_type
          return null;
        case 5: // CAL_BUMP
          return null;
        default:
          return null;
      }
    }
  };

  /**
   * Helper: Save chunks in batches
   */
  const saveChunksBatch = async (
    logApi: any,
    logId: string,
    serialNumber: string,
    dataPackets: any[],
    numPackets: number,
    fileType: number,
    isMini: boolean,
    logTypeName: string
  ) => {
    const BATCH_SIZE = 200;
    const validChunks: Array<{ chunk_index: number; chunk_data: number[] }> = [];
    const missingChunkIndices: number[] = [];

    // Map chunks by index
    const chunkMap = new Map<number, any>();
    for (let i = 0; i < dataPackets.length; i++) {
      const chunkJson = dataPackets[i];
      let chunkIndex: number;

      if (isMini) {
        if (fileType === 0) {
          chunkIndex =
            typeof chunkJson.datalog_chunk_index === 'number' ? chunkJson.datalog_chunk_index : i;
        } else if (fileType === 1) {
          chunkIndex =
            typeof chunkJson.eventlog_chunk_index === 'number' ? chunkJson.eventlog_chunk_index : i;
        } else {
          chunkIndex =
            typeof chunkJson.eventlog_chunk_index === 'number' ? chunkJson.eventlog_chunk_index : i;
        }
      } else {
        if (typeof chunkJson.chunk_index === 'number') {
          chunkIndex = chunkJson.chunk_index;
        } else if (fileType === 0) {
          chunkIndex =
            typeof chunkJson['datalog chunk index'] === 'number'
              ? chunkJson['datalog chunk index']
              : i;
        } else if (fileType === 1) {
          chunkIndex =
            typeof chunkJson['event chunk index'] === 'number' ? chunkJson['event chunk index'] : i;
        } else if (fileType === 4) {
          chunkIndex =
            typeof chunkJson['alarm chunk index'] === 'number' ? chunkJson['alarm chunk index'] : i;
        } else if (fileType === 5) {
          chunkIndex =
            typeof chunkJson['cal/bump chunk index'] === 'number'
              ? chunkJson['cal/bump chunk index']
              : i;
        } else {
          chunkIndex = i;
        }
      }

      chunkMap.set(chunkIndex, chunkJson);
    }

    // Collect valid chunks
    for (let expectedIndex = 0; expectedIndex < numPackets; expectedIndex++) {
      const chunkJson = chunkMap.get(expectedIndex);

      if (!chunkJson) {
        missingChunkIndices.push(expectedIndex);
        continue;
      }

      let chunkData: number[] | undefined;

      if (isMini) {
        if (fileType === 0) {
          chunkData = chunkJson.datalog_chunk as number[] | undefined;
        } else if (fileType === 1) {
          chunkData = chunkJson.eventlog_chunk as number[] | undefined;
        } else if (fileType === 2) {
          chunkData = chunkJson.eventlog_binary as number[] | undefined;
        } else {
          chunkData = chunkJson.eventlog_binary as number[] | undefined;
        }
      } else {
        if (fileType === 0) {
          chunkData = chunkJson.chunk_data || chunkJson['datalog chunk(binary)'];
        } else if (fileType === 1) {
          chunkData = chunkJson.chunk_data || chunkJson['event chunk(binary)'];
        } else if (fileType === 2) {
          chunkData = chunkJson.chunk_data || chunkJson['gen conf chunk(binary)'];
        } else if (fileType === 3) {
          chunkData = chunkJson.chunk_data || chunkJson['sen conf chunk(binary)'];
        } else if (fileType === 4) {
          chunkData = chunkJson.chunk_data || chunkJson['alarm chunk(binary)'];
        } else if (fileType === 5) {
          chunkData = chunkJson.chunk_data || chunkJson['cal/bump chunk(binary)'];
        } else {
          chunkData = chunkJson.chunk_data;
        }
      }

      if (!chunkData || !Array.isArray(chunkData)) {
        missingChunkIndices.push(expectedIndex);
        continue;
      }

      // log_type validation (LITE only)
      if (!isMini && chunkData.length >= 3) {
        const logType = chunkData[2];
        const expectedLogType = getExpectedLogType(fileType, false);

        if (expectedLogType !== null && logType !== expectedLogType) {
          console.warn(
            `⚠️ Chunk ${expectedIndex} log_type mismatch. Expected: 0x${expectedLogType.toString(16)}, Actual: 0x${logType.toString(16)}. Marking as missing.`
          );
          missingChunkIndices.push(expectedIndex);
          continue;
        }
      }

      validChunks.push({
        chunk_index: expectedIndex,
        chunk_data: chunkData,
      });
    }

    // Save in batches
    const batchPromises: Promise<void>[] = [];
    let savedChunkCount = 0;
    let lastProgressUpdate = 0;

    for (let i = 0; i < validChunks.length; i += BATCH_SIZE) {
      const batch = validChunks.slice(i, i + BATCH_SIZE);

      const promise = logApi
        .saveLogChunkBatch({
          log_id: logId,
          serial_number: serialNumber,
          chunks: batch,
        })
        .then(() => {
          savedChunkCount += batch.length;
          const progressPercent = Math.round((savedChunkCount / numPackets) * 100);

          if (progressPercent - lastProgressUpdate >= 5 || savedChunkCount === validChunks.length) {
            lastProgressUpdate = progressPercent;
            showLoading(
              `${logTypeName}\nSaving to cloud ${progressPercent}%\n(${savedChunkCount}/${numPackets})`
            );
          }
        });

      batchPromises.push(promise);
    }

    await Promise.all(batchPromises);
  };

  /**
   * Helper: Save compressed chunks batch (FW >= 5.0.0)
   * 압축된 gas reading 데이터를 새 API 엔드포인트로 전송
   */
  const saveChunksBatchCompressed = async (
    logApi: any,
    logId: string,
    serialNumber: string,
    dataPackets: any[],
    numPackets: number,
    deviceFwVersion: string,
    logTypeName: string
  ) => {
    const BATCH_SIZE = 200;
    const validChunks: Array<{ chunk_index: number; chunk_data: number[] }> = [];
    const missingChunkIndices: number[] = [];

    // Map chunks by index (LITE gas reading - fileType 0)
    const chunkMap = new Map<number, any>();
    for (let i = 0; i < dataPackets.length; i++) {
      const chunkJson = dataPackets[i];
      let chunkIndex: number;

      // LITE 기기의 gas reading (fileType 0) 청크 인덱스 추출
      if (typeof chunkJson.chunk_index === 'number') {
        chunkIndex = chunkJson.chunk_index;
      } else if (typeof chunkJson['datalog chunk index'] === 'number') {
        chunkIndex = chunkJson['datalog chunk index'];
      } else {
        chunkIndex = i;
      }

      chunkMap.set(chunkIndex, chunkJson);
    }

    // Collect valid chunks
    for (let expectedIndex = 0; expectedIndex < numPackets; expectedIndex++) {
      const chunkJson = chunkMap.get(expectedIndex);

      if (!chunkJson) {
        if (expectedIndex < 5) {
          console.warn(`[saveChunksBatchCompressed] 청크 ${expectedIndex} 누락: chunkMap에 없음`);
        }
        missingChunkIndices.push(expectedIndex);
        continue;
      }

      const chunkData = chunkJson.chunk_data || chunkJson['datalog chunk(binary)'];

      if (!chunkData || !Array.isArray(chunkData)) {
        if (expectedIndex < 5) {
          console.warn(
            `[saveChunksBatchCompressed] 청크 ${expectedIndex} 누락: chunk_data 없음 또는 배열 아님`
          );
        }
        missingChunkIndices.push(expectedIndex);
        continue;
      }

      validChunks.push({
        chunk_index: expectedIndex,
        chunk_data: chunkData,
      });
    }

    // Save in batches using compressed API
    const batchPromises: Promise<void>[] = [];
    let savedChunkCount = 0;
    let lastProgressUpdate = 0;

    for (let i = 0; i < validChunks.length; i += BATCH_SIZE) {
      const batch = validChunks.slice(i, i + BATCH_SIZE);

      const promise = logApi
        .saveLogChunkBatchCompressed({
          log_id: logId,
          serial_number: serialNumber,
          device_fw_version: deviceFwVersion,
          chunks: batch,
        })
        .then(() => {
          savedChunkCount += batch.length;
          const progressPercent = Math.round((savedChunkCount / numPackets) * 100);

          if (progressPercent - lastProgressUpdate >= 5 || savedChunkCount === validChunks.length) {
            lastProgressUpdate = progressPercent;
            showLoading(
              `${logTypeName}\nSaving compressed data to cloud ${progressPercent}%\n(${savedChunkCount}/${numPackets})`
            );
          }
        });

      batchPromises.push(promise);
    }

    await Promise.all(batchPromises);
  };

  /**
   * Helper: Handle missing chunks
   */
  const handleMissingChunks = async (
    connection: any,
    logApi: any,
    logId: string,
    serialNumber: string,
    numPackets: number,
    fileType: number,
    isMini: boolean,
    deviceInfo: any,
    logTypeName: string
  ) => {
    const missingChunks = await logApi.getMissingChunks(logId);

    if (missingChunks.length === 0) {
      return;
    }

    // FW 5.0.0+ 압축 데이터 확인
    const deviceFwVersion = deviceInfo?.fw_version || deviceInfo?.FW_Version;
    const isGasReading = fileType === 0;
    const useCompressedApi = !isMini && isGasReading && isCompressedGasReading(deviceFwVersion);

    const requestFunctionId = isMini
      ? MINI_FILE_TYPE_TO_REQUEST_FUNCTION_ID[
          fileType as keyof typeof MINI_FILE_TYPE_TO_REQUEST_FUNCTION_ID
        ]
      : FILE_TYPE_TO_REQUEST_FUNCTION_ID[fileType as Exclude<FileType, FileType.DATALOG>] || 0;

    const chunkIndexParamName = isMini
      ? requestFunctionId === 0x03
        ? 'datalog_chunk_index'
        : 'eventlog_chunk_index'
      : 'chunk_index';

    const failedChunkIndices: number[] = [];
    const retriedChunks: Array<{ chunk_index: number; chunk_data: number[] }> = [];

    for (const chunkIndex of missingChunks) {
      showLoading(
        `${logTypeName}\nRequesting missing chunk ${chunkIndex}...\n(${missingChunks.indexOf(chunkIndex) + 1}/${missingChunks.length})`
      );

      try {
        const retryResponse = await connection.connectDevice(
          {
            class_id: 16,
            function_id: requestFunctionId,
            json_payload: {
              [chunkIndexParamName]: chunkIndex,
            },
          },
          deviceInfo
        );

        const retryChunkJson = retryResponse.json as any;
        let retryChunkData: number[] | undefined;

        if (isMini) {
          if (fileType === 0) {
            retryChunkData = retryChunkJson.datalog_chunk as number[] | undefined;
          } else if (fileType === 1) {
            retryChunkData = retryChunkJson.eventlog_chunk as number[] | undefined;
          } else if (fileType === 2) {
            retryChunkData = retryChunkJson.eventlog_binary as number[] | undefined;
          } else {
            retryChunkData = retryChunkJson.eventlog_binary as number[] | undefined;
          }
        } else {
          if (fileType === 0) {
            retryChunkData = retryChunkJson.chunk_data || retryChunkJson['datalog chunk(binary)'];
          } else if (fileType === 1) {
            retryChunkData = retryChunkJson.chunk_data || retryChunkJson['event chunk(binary)'];
          } else if (fileType === 4) {
            retryChunkData = retryChunkJson.chunk_data || retryChunkJson['alarm chunk(binary)'];
          } else if (fileType === 5) {
            retryChunkData = retryChunkJson.chunk_data || retryChunkJson['cal/bump chunk(binary)'];
          } else {
            retryChunkData = retryChunkJson.chunk_data;
          }
        }

        if (retryChunkData && Array.isArray(retryChunkData) && retryChunkData.length > 0) {
          retriedChunks.push({
            chunk_index: chunkIndex,
            chunk_data: retryChunkData,
          });
        } else {
          console.warn(`⚠️ Chunk ${chunkIndex} retry failed: no data`);
          failedChunkIndices.push(chunkIndex);
        }
      } catch (error) {
        console.error(`❌ Chunk ${chunkIndex} retry failed:`, error);
        failedChunkIndices.push(chunkIndex);
      }
    }

    // 재시도한 청크들을 배치로 저장
    if (retriedChunks.length > 0) {
      showLoading(`${logTypeName}\nSaving ${retriedChunks.length} missing chunks...`);

      if (useCompressedApi) {
        // 압축 데이터: 압축 API 사용
        await logApi.saveLogChunkBatchCompressed({
          log_id: logId,
          serial_number: serialNumber,
          device_fw_version: deviceFwVersion,
          chunks: retriedChunks,
        });
      } else {
        // 비압축 데이터: 개별 저장 (기존 로직)
        for (const chunk of retriedChunks) {
          await logApi.saveLogChunk({
            log_id: logId,
            serial_number: serialNumber,
            chunk_index: chunk.chunk_index,
            chunk_data: chunk.chunk_data,
          });
        }
      }
    }

    if (failedChunkIndices.length > 0) {
      const errorMessage = `Chunk count mismatch. Expected: ${numPackets}, Actual: ${numPackets - failedChunkIndices.length}`;
      await logApi.failLog(logId, errorMessage);
      throw new Error(errorMessage);
    }
  };

  return {
    handleBluetoothConnect,
    handleCradleConnect,
    handleDisconnect,
    handleLoadDevice,
    handleScanAnother,
    refreshDeviceInfo,
    saveDeviceConfig,
    loadDefaultConfig,
    downloadDatalog,
  };
};
